import { Colors, Text } from '@amelio/ui-library';

import { useEmployeeObjectivesInPeriod } from './useEmployeeObjectivesInPeriod';

import { EmployeeObjectivesOverview } from '@/components/objectives/EmployeeObjectivesOverview';
import PeriodSelect from '@/components/objectives/PeriodSelect';
import { useAmelioTranslations } from '@/hooks';

interface ObjectivesTabProps {
    employeeId: string;
    /**
     * The period the whole profile is read through, owned by the page so that this tab and the
     * summary's objectives card never disagree. `null` means "the page's default period".
     */
    pickedPeriodId: string | null;
    onPickPeriod: (periodId: string) => void;
    dataTestId?: string;
}

/**
 * The Objectives tab of the performance profile: a period lens, the overall progression, four
 * headline tiles and the objectives the employee owns or takes part in inside that period. The
 * server does the filtering (employee and period alike); the tab only counts what it is given.
 */
export const ObjectivesTab = ({
    employeeId,
    pickedPeriodId,
    onPickPeriod,
    dataTestId = 'objectives-tab',
}: ObjectivesTabProps) => {
    const { t } = useAmelioTranslations();

    const {
        periodOptions,
        periodId,
        isLoadingPeriods,
        objectives,
        isLoadingObjectives,
        summary,
        isSummaryKnown,
        isError,
    } = useEmployeeObjectivesInPeriod({ employeeId, pickedPeriodId });

    return (
        // This gap separates the period lens from the block below it; the block's own inner
        // rhythm belongs to `EmployeeObjectivesOverview`, which both readers of it share.
        <div
            className={'flex flex-col gap-8 md:gap-12'}
            data-testid={dataTestId}
        >
            {(isLoadingPeriods || periodOptions.length > 0) && (
                <div className={'flex flex-wrap items-center justify-end gap-3 px-2'}>
                    <span className={'text-obj-muted text-xs font-semibold'}>
                        {t('performanceProfile.objectives.period.label')}
                    </span>
                    <PeriodSelect
                        options={periodOptions}
                        value={periodId}
                        onChange={onPickPeriod}
                        isLoading={isLoadingPeriods}
                    />
                </div>
            )}

            {isError ? (
                <Text color={Colors.ERROR}>
                    {t('performanceProfile.assessmentObjectives.loadError')}
                </Text>
            ) : (
                <EmployeeObjectivesOverview
                    objectives={objectives}
                    summary={summary}
                    isSummaryKnown={isSummaryKnown}
                    isLoadingObjectives={isLoadingObjectives}
                    windowKey={periodId}
                />
            )}
        </div>
    );
};
